<template>
    <div
        class="w-100 d-flex flex-column"
        style="overflow: hidden; min-height: 100vh;"
    >
        <div class="setup-container">
            <!-- Filters Section -->
            <div class="filters-section">
                <!-- Optimization Name -->
                <div class="filter-panel d-flex flex-column mt-3">
                    <h4>{{ $t("route_optimization.optimization_name_optional") }}</h4>
                    <div class="row g-2">
                        <div class="col-lg-12 col-md-12 col-sm-12">
                            <input
                                v-model="optimizationName"
                                type="text"
                                class="form-control form-control-sm"
                                :placeholder="$t('route_optimization.optimization_name_placeholder')"
                                :disabled="!!resubmitJobId"
                                :title="resubmitJobId ? $t('route_optimization.name_locked_resubmit') : ''"
                            >
                            <div
                                v-if="resubmitJobId"
                                class="info-box mt-4 col-12"
                            >
                                <font-awesome-icon
                                    icon="info-circle"
                                    class="info-circle"
                                />
                                <span>{{ $t('route_optimization.name_locked_resubmit') }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Operation Type Selection -->
                <div class="filter-panel d-flex flex-column">
                    <div class="d-flex align-items-center gap-2 mb-3">
                        <h4 class="mb-0">
                            {{ $t('route_optimization.operation_type') }}
                        </h4>
                        <InfoPopover
                            placement="right"
                            :items="operationTypeInfoItems"
                        />
                    </div>
                    <div class="operation-type-pills">
                        <span
                            v-for="op in operationTypes"
                            :key="op.value"
                            class="routa-pill"
                            :class="{ active: operationType === op.value }"
                            @click="operationType = op.value"
                        >
                            {{ $t(op.labelKey) }}
                        </span>
                    </div>
                </div>

                <!-- Geometry Selection -->
                <div class="filter-panel d-flex flex-column">
                    <h4>{{ $t("route_optimization.geometry_selection") }}</h4>

                    <div class="row g-2">
                        <div class="col-6 col-lg-4">
                            <RouteOptimizationContractSelector
                                ref="contractSelector"
                                v-model="selectedContract"
                                :from="currentDate"
                                :to="currentDate"
                                @loading="$store.commit('routeOptimization/setContractsLoading', $event)"
                                @contracts-loaded="$store.commit('routeOptimization/setAvailableContracts', $event)"
                            />
                        </div>

                        <div class="col-6 col-lg-4">
                            <RouteOptimizationOrderSelector
                                ref="orderSelector"
                                v-model="selectedOrder"
                                :contract="selectedContract"
                                :initial-orders="availableOrders"
                                @loading="$store.commit('routeOptimization/setOrdersLoading', $event)"
                                @orders-loaded="$store.commit('routeOptimization/setAvailableOrders', $event)"
                            />
                        </div>

                        <div class="col-md-1 pt-3 d-none d-lg-flex align-items-center justify-content-center">
                            <span class="text-muted"> -- {{ $t("route_optimization.or") }} -- </span>
                        </div>

                        <div class="col-12 col-lg-3">
                            <label class="input-title">{{ $t("route_optimization.select_geojson") }}</label>

                            <input
                                ref="fileInput"
                                type="file"
                                class="form-control form-control-sm"
                                :class="{ 'is-invalid': showValidation && loadedGeometries.filter(g => g.selected).length === 0 }"
                                accept=".geojson,application/geo+json,application/json"
                                @change="handleFileChange"
                            >
                        </div>
                    </div>

                    <!-- Trip Search Section -->
                    <div class="mt-3">
                        <label class="input-title d-block">{{ $t('route_optimization.select_route_from_trips') }}</label>
                        <button
                            class="btn btn-sm btn-outline-success"
                            style="min-width: 120px;"
                            :disabled="contractsLoading || ordersLoading"
                            @click="openTripSearchModal"
                        >
                            {{ $t('route_optimization.search_trips') }}
                        </button>
                    </div>
                </div>

                <!-- Route Mask Status -->

                <!-- Geometry Preview Map -->
                <div class="filter-panel d-flex flex-column">
                    <div class="d-flex align-items-center justify-content-between mb-3">
                        <h4 class="mb-0">
                            {{ $t("route_optimization.geometry_preview") }}
                        </h4>
                        <button
                            class="btn btn-sm btn-danger"
                            @click="clearGeometry"
                        >
                            {{ $t("route_optimization.clear") }}
                        </button>
                    </div>

                    <div class="geometry-preview-container">
                        <!-- Map -->
                        <div class="geometry-map-wrapper">
                            <map-container
                                ref="mapContainer"
                                :find-user="false"
                                :menu-enabled="false"
                                @on-map-changed="onPreviewMapReady"
                                @on-map-clicked="handleMapClick"
                            />
                            <!-- Route mask loading/error overlay -->
                            <div
                                v-if="routeMaskLoading || routeMaskError"
                                class="route-mask-status"
                            >
                                <template v-if="routeMaskLoading">
                                    <div
                                        class="spinner-border spinner-border-sm spinner-routa"
                                        role="status"
                                    />
                                    <span>{{ $t('route_optimization.route_mask_loading') }}</span>
                                </template>
                                <span
                                    v-else-if="routeMaskError"
                                    class="text-danger"
                                >{{ $t('route_optimization.route_mask_error') }}</span>
                            </div>
                            <!-- Map legend -->
                            <div
                                v-if="loadedGeometries.some(g => g.selected)"
                                class="preview-legend"
                            >
                                <div
                                    v-for="item in loadedGeometries.filter(g => g.selected)"
                                    :key="item.name"
                                    class="preview-legend-item"
                                >
                                    <div
                                        class="preview-legend-color"
                                        :style="{ background: item.color || '#00C896' }"
                                    />
                                    <span class="preview-legend-label">{{ item.name }}</span>
                                </div>
                                <div
                                    v-if="routeMaskGeometry || routeMaskLoading"
                                    class="preview-legend-item"
                                >
                                    <div
                                        class="preview-legend-color preview-legend-color--mask"
                                        :style="{ opacity: routeMaskLoading ? 0.4 : 1 }"
                                    />
                                    <span class="preview-legend-label">{{ $t('route_optimization.route_mask') }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Geometry layer list — overlaid on top-left of map -->
                        <div
                            v-if="loadedGeometries.length > 0"
                            class="geometry-list"
                        >
                            <!-- List header: title, counts -->
                            <div class="geometry-list-header">
                                <div class="geometry-list-title-row">
                                    <span class="geometry-list-title">{{ $t("route_optimization.optimizable_routes") }}</span>
                                    <span class="geometry-list-counts">
                                        {{ $t("route_optimization.geometries_loaded", { count: loadedGeometries.length }) }}
                                        &nbsp;·&nbsp;
                                        {{ $t("route_optimization.geometries_typed", {
                                            count: loadedGeometries.filter(g => g.selected && g.route_type != null).length
                                        }) }}
                                    </span>
                                    <span class="geometry-total-km ms-auto">{{ totalGeometryKilometers }}</span>
                                </div>
                                <div
                                    class="geometry-list-subrow"
                                    @click="geometryListExpanded = !geometryListExpanded"
                                >
                                    <span class="geometry-list-subtext">{{ $t("route_optimization.set_type_for_all") }}</span>
                                    <font-awesome-icon
                                        :icon="geometryListExpanded ? 'chevron-down' : 'chevron-right'"
                                        class="geometry-list-chevron ms-auto"
                                    />
                                </div>
                            </div>

                            <div
                                class="geometry-items-wrapper"
                                :class="{ collapsed: !geometryListExpanded }"
                            >
                                <div
                                    v-show="geometryListExpanded"
                                    class="geometry-items"
                                >
                                    <div
                                        v-for="(item, index) in loadedGeometries"
                                        :key="index"
                                        class="geometry-item"
                                        :class="{ 'geometry-item--deselected': !item.selected && !item.disabled }"
                                        @mouseenter="highlightGeometryOnMap(index)"
                                        @mouseleave="unhighlightGeometryOnMap(index)"
                                    >
                                        <input
                                            :id="'geom-' + index"
                                            type="checkbox"
                                            class="form-check-input flex-shrink-0"
                                            :checked="item.selected"
                                            :disabled="item.disabled"
                                            @change="toggleGeometrySelection(index, $event.target.checked)"
                                        >
                                        <span
                                            class="geometry-color-dot"
                                            :style="{ background: item.color || '#00C896' }"
                                        />
                                        <label
                                            :for="'geom-' + index"
                                            class="form-check-label geometry-item-name"
                                            :class="{ 'text-muted': item.disabled || !item.selected }"
                                            :title="item.name"
                                        >
                                            {{ item.name || item.description || `Geometry ${index + 1}` }}
                                        </label>
                                        <div
                                            v-if="item.selected && !item.disabled"
                                            class="route-type-toggle ms-auto"
                                        >
                                            <span
                                                class="badge route-type-badge"
                                                :class="{ active: (item.route_type ?? 1) === 1 || (item.route_type ?? 1) === 3 }"
                                                @click="toggleGeometryRouteType(index, 1)"
                                            >
                                                <font-awesome-icon
                                                    icon="road"
                                                    class="me-1"
                                                />
                                                {{ $t("route_optimization.route_type_road") }}
                                            </span>
                                            <span
                                                class="badge route-type-badge route-type-badge--sidewalk"
                                                :class="{ active: (item.route_type ?? 1) === 2 || (item.route_type ?? 1) === 3 }"
                                                @click="toggleGeometryRouteType(index, 2)"
                                            >
                                                <font-awesome-icon
                                                    icon="person-walking"
                                                    class="me-1"
                                                />
                                                {{ $t("route_optimization.route_type_sidewalk") }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div><!-- /geometry-items-wrapper -->
                        </div>
                    </div>
                </div>

                <!-- Max Duration / time limit parameters -->
                <div class="filter-panel d-flex flex-column">
                    <h4>{{ $t("route_optimization.max_duration_label_optional") }}</h4>

                    <div class="row g-2">
                        <div class="col-6 col-lg-3">
                            <label class="form-label small mb-1">
                                {{ $t("route_optimization.max_duration_hours_label") }}
                            </label>
                            <input
                                v-model.number="maxDurationHoursInput"
                                type="number"
                                min="0"
                                step="1"
                                class="form-control form-control-sm"
                            >
                        </div>
                        <div class="col-6 col-lg-3">
                            <label class="form-label small mb-1">
                                {{ $t("route_optimization.max_duration_minutes_label") }}
                            </label>
                            <input
                                v-model.number="maxDurationMinutesInput"
                                type="number"
                                min="0"
                                max="59"
                                step="1"
                                class="form-control form-control-sm"
                            >
                        </div>
                    </div>
                    <div class="row">
                        <div class="info-box mt-4 col-lg-6 col-md-12 col-sm-12 ms-2">
                            <font-awesome-icon
                                icon="info-circle"
                                class="info-circle"
                            />
                            <span>{{ $t("route_optimization.max_duration_help") }}</span>
                        </div>
                    </div>
                </div>

                <!-- Vehicle Parameters -->
                <div class="filter-panel d-flex flex-column">
                    <VehicleManager
                        v-if="!isMaxDurationMode"
                        v-model="vehicles"
                        :show-validation="showValidation"
                        :selected-geometry="firstLayerGeometry"
                        :operation-type="operationType"
                    />
                    <SingleVehicleEditor
                        v-else
                        v-model="singleVehicle"
                        :show-validation="showValidation"
                        :selected-geometry="firstLayerGeometry"
                        :operation-type="operationType"
                    />
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-button-container row mt-4">
                <div class="col-12 d-flex justify-content-end">
                    <button
                        v-if="hasSimulation || resubmitJobId"
                        class="btn btn-secondary btn-m me-3"
                        @click="showConfirmModal=true"
                    >
                        {{ $t("route_optimization.confirm_reset") }}
                    </button>

                    <button
                        v-if="!hasSimulation && !resubmitJobId && hasAnyData"
                        class="btn btn-outline-secondary btn-m me-3"
                        @click="showConfirmModal=true"
                    >
                        {{ $t("route_optimization.clear") }}
                    </button>

                    <button
                        v-if="!hasSimulation"
                        class="btn btn-primary btn-m me-3"
                        @click="startOptimization"
                    >
                        {{ $t("route_optimization.start_optimizing") }}
                    </button>

                    <button
                        v-if="hasSimulation"
                        class="btn btn-primary btn-m"
                        @click="$emit('go-to-simulation')"
                    >
                        {{ $t("route_optimization.view_simulation") }} →
                    </button>
                </div>
            </div>
        </div>
    </div>

    <BaseModal
        v-if="showConfirmModal"
        :hide-footer="true"
        :title="$t('route_optimization.confirm_reset')"
        @close="showConfirmModal=false"
    >
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-body">
                    <h5
                        id="modalTitle"
                        class="modal-title justify-content-center text-center"
                    >
                        {{ $t('route_optimization.reset_optimization') }}
                    </h5>
                </div>
                <div class="modal-footer justify-content-center mb-2 mt-2">
                    <button
                        type="button"
                        class="btn btn-secondary me-2"
                        style="min-width: 100px;"
                        @click="showConfirmModal=false"
                    >
                        {{ $t('common.no') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        style="min-width: 100px;"
                        @click="resetOptimizationState()"
                    >
                        {{ $t('common.yes') }}
                    </button>
                </div>
            </div>
        </div>
    </BaseModal>


    <transition name="fade">
        <div
            v-if="optimizerLoading"
            class="loading-modal"
        >
            <div class="loading-content">
                <div class="d-flex align-items-center gap-3">
                    <p class="mb-0">
                        {{ $t("route_optimization.optimizing_route") || "Optimizing route..." }}
                    </p>
                    <div
                        class="spinner-border spinner-routa"
                        role="status"
                    >
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div class="info-box mt-5">
                    <p class="mb-0">
                        <strong>{{ $t("route_optimization.note") || "Note:" }}</strong> {{ $t("route_optimization.optimization_time_info") }}
                    </p>
                </div>
            </div>
        </div>
    </transition>

    <!-- Search Trips Modal -->
    <TripSearchModal
        :show="showSearchTripsModal"
        :available-contracts="availableContracts"
        :available-orders="availableOrders"
        :selected-contract="selectedContract"
        :selected-orders="selectedOrder ? [selectedOrder] : []"
        @close="closeTripSearchModal"
        @routes-selected="handleRoutesSelected"
    />
</template>

<script>
import { inject } from "vue";
import { mapState, mapGetters } from "vuex";
import RouteOptimizationContractSelector from "./RouteOptimizationContractSelector.vue";
import RouteOptimizationOrderSelector from "./RouteOptimizationOrderSelector.vue";
import TripSearchModal from "./trips/TripSearchModal.vue";
import SingleVehicleEditor from "./SingleVehicleEditor.vue";
import MapContainer from "@/components/map/MapContainer.vue";
import { routeOptimizationMixin } from "../mixins/RouteOptimizationMixin";
import BaseModal from "@/components/BaseModal.vue";
import VehicleManager from "@/components/routeoptimization/VehicleManager.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import InfoPopover from '@/components/common/InfoPopover.vue'

export default {
    name: "RouteOptimizationSetup",
    components: {
        FontAwesomeIcon,
        RouteOptimizationContractSelector,
        RouteOptimizationOrderSelector,
        TripSearchModal,
        VehicleManager,
        SingleVehicleEditor,
        MapContainer,
        BaseModal,
        InfoPopover,
    },
    mixins: [routeOptimizationMixin],
    props: {
        opt: {
            type: Object,
            required: true,
        },
        hasSimulation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:opt", "file-change", "go-to-simulation"],
    setup() {
        const showAlert = inject("showAlert");
        return { showAlert };
    },
    data() {
        return {
            mapCenter: { lat: 62.2426, lon: 25.7482 },
            previewMap: null,
            mapSelectionMode: false,
            showConfirmModal: false,
            maxDurationHoursInput: null,
            maxDurationMinutesInput: null,
            optimizationName: "",
            showRouteTypeSelection: false,
            geometryListExpanded: true,
            operationTypes: [
                { value: 'winter_maintenance', labelKey: 'route_optimization.winter_maintenance' },
                { value: 'road_inspection',    labelKey: 'route_optimization.road_inspection' },
            ],
        };
    },
    computed: {
        resubmitJobId() {
            return this.$store.state.routeOptimization.resubmitJobId;
        },
        currentDate() {
            return new Date().toISOString().split("T")[0];
        },
        operationType: {
            get() { return this.$store.state.routeOptimization.operationType },
            set(value) { this.$store.commit('routeOptimization/setOperationType', value) }
        },
        isRoadInspection() {
            return this.operationType === 'road_inspection'
        },
        operationTypeInfoItems() {
            return [
                {
                    title: this.$t('route_optimization.info_winter_maintenance_title'),
                    content: this.$t('route_optimization.info_winter_maintenance_content')
                },
                {
                    title: this.$t('route_optimization.info_road_inspection_title'),
                    content: this.$t('route_optimization.info_road_inspection_content')
                }
            ]
        },
        ...mapState("routeOptimization", {
            loadedGeometries: (state) => state.loadedGeometries,
            geometrySource: (state) => state.geometrySource,
            showValidation: (state) => state.showValidation,
            showSearchTripsModal: (state) => state.showSearchTripsModal,
            contractsLoading: (state) => state.contractsLoading,
            ordersLoading: (state) => state.ordersLoading,
            availableContracts: (state) => state.availableContracts,
            availableOrders: (state) => state.availableOrders,
            optimizerLoading: (state) => state.optimizerLoading,
            navigationContractId: (state) => state.navigationContractId,
            navigationOrderId: (state) => state.navigationOrderId,
            routeMaskGeometry: (state) => state.routeMaskGeometry,
            routeMaskLoading: (state) => state.routeMaskLoading,
            routeMaskError: (state) => state.routeMaskError,
        }),
        ...mapGetters("routeOptimization", ["canOptimize", "allSelected", "totalGeometryKilometers"]),
        selectedContract: {
            get() {
                return this.$store.state.routeOptimization.selectedContract;
            },
            set(value) {
                this.$store.commit("routeOptimization/setSelectedContract", value);
            },
        },
        selectedOrder: {
            get() {
                return this.$store.state.routeOptimization.selectedOrder;
            },
            set(value) {
                this.$store.dispatch("routeOptimization/selectOrder", {
                    order: value,
                    routeLabel: this.$t('route_optimization.route_label_default'),
                });
                if (value && this.$refs.fileInput) {
                    this.$refs.fileInput.value = "";
                }
            },
        },
        vehicles: {
            get() {
                return this.$store.state.routeOptimization.vehicles;
            },
            set(value) {
                this.$store.commit("routeOptimization/setVehicles", value);
            },
        },
        singleVehicle: {
            get() {
                return this.$store.state.routeOptimization.singleVehicle;
            },
            set(value) {
                this.$store.commit("routeOptimization/setSingleVehicle", value);
            },
        },
        hasAnyData() {
            return !!(this.loadedGeometries.length > 0 || this.vehicles.some(v => v.properties?.work_width || v.properties?.max_speed || v.properties?.work_speed) || this.maxDurationHoursInput || this.maxDurationMinutesInput || this.optimizationName);
        },
        // Flat FeatureCollection from the first selected layer, used by VehicleManager and
        // SingleVehicleEditor to derive a default vehicle start coordinate from the route.
        firstLayerGeometry() {
            const first = this.loadedGeometries.find((g) => g.selected && !g.disabled)
            return first?.geometry || null
        },
        // Returns shared route_type if all selected geometries have the same value, else null
        sharedRouteType() {
            const selected = this.loadedGeometries.filter(g => g.selected && !g.disabled);
            if (!selected.length) return null;
            const first = selected[0].route_type ?? 1;
            return selected.every(g => (g.route_type ?? 1) === first) ? first : null;
        },
        isFormValid() {
            const hasGeometry = this.loadedGeometries.some(g => g.selected);
            if (!hasGeometry) return false;
            if (this.isMaxDurationMode) return this.validateVehicle(this.singleVehicle);
            return this.vehicles.every(v => this.validateVehicle(v));
        },
        selectedTripsCount() {
            return this.$refs.tripSearch?.selectedTrips?.length || 0;
        },
        isMaxDurationMode() {
            return (this.maxDurationHoursInput !== null && this.maxDurationHoursInput !== '' && this.maxDurationHoursInput !== undefined && this.maxDurationHoursInput > 0) ||
                   (this.maxDurationMinutesInput !== null && this.maxDurationMinutesInput !== '' && this.maxDurationMinutesInput !== undefined && this.maxDurationMinutesInput > 0);
        },
    },
    watch: {
        maxDurationHoursInput(val) {
            this.$store.commit("routeOptimization/setMaxDurationHours", val);
        },
        maxDurationMinutesInput(val) {
            this.$store.commit("routeOptimization/setMaxDurationMinutes", val);
        },
        isMaxDurationMode(isMaxMode) {
            // Initialize single vehicle when entering max duration mode for the first time.
            // Kept here (not in the computed) to avoid side effects inside a computed property.
            if (isMaxMode && !this.singleVehicle) {
                const startCoords = this.getRouteStartCoords();
                const isRoadInspection = this.operationType === 'road_inspection';
                const defaultVehicle = {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: startCoords },
                    properties: isRoadInspection
                        ? { max_speed: 160, work_width: 0, work_speed: 0, include_return_trip: false, type: 'vehicle' }
                        : { work_width: 400, max_speed: 80, work_speed: 25, include_return_trip: true, type: 'vehicle' }
                };
                this.$store.commit('routeOptimization/setSingleVehicle', defaultVehicle);
            }
        },
        optimizationName(val) {
            this.$store.commit("routeOptimization/setOptimizationName", val);
        },
        loadedGeometries: {
            handler(newVal) {
                if (this.previewMap) {
                    this.renderGeometry();
                }
            },
            deep: true,
        },
        '$store.state.routeOptimization.loadedGeometries'(newVal) {
            // Auto-expand whenever a new geometry array is committed to the store
            if (newVal && newVal.length > 0) {
                this.geometryListExpanded = true;
            }
        },
        routeMaskGeometry() {
            if (this.previewMap) {
                this.renderGeometry();
            }
        },
        availableContracts() {
            this.checkNavigationData();
        },
        availableOrders() {
            this.checkNavigationData();
        },
        '$store.state.routeOptimization.maxDurationHours'(newVal) {
            if (newVal !== this.maxDurationHoursInput) {
                this.maxDurationHoursInput = newVal;
            }
        },
        '$store.state.routeOptimization.maxDurationMinutes'(newVal) {
            if (newVal !== this.maxDurationMinutesInput) {
                this.maxDurationMinutesInput = newVal;
            }
        },
        '$store.state.routeOptimization.optimizationName'(newVal) {
            if (newVal !== this.optimizationName) {
                this.optimizationName = newVal || '';
            }
        },
    },
    activated() {
        if (this.previewMap) {
            this.$nextTick(() => this.renderGeometry());
        }
        this.maxDurationHoursInput = this.$store.state.routeOptimization.maxDurationHours;
        this.maxDurationMinutesInput = this.$store.state.routeOptimization.maxDurationMinutes;
        this.optimizationName = this.$store.state.routeOptimization.optimizationName || "";
        this.$nextTick(() => this.checkNavigationData());
    },
    mounted() {
        this.maxDurationHoursInput = this.$store.state.routeOptimization.maxDurationHours;
        this.maxDurationMinutesInput = this.$store.state.routeOptimization.maxDurationMinutes;
        this.optimizationName = this.$store.state.routeOptimization.optimizationName || "";
        this.$nextTick(() => this.checkNavigationData());
    },

    methods: {
        getRouteStartCoords() {
            const selected = this.loadedGeometries.filter((g) => g.selected)
            if (!selected.length) return [25.7482, 62.2426]
            const features = selected[0]?.geometry?.features
            if (!features?.length) return [25.7482, 62.2426]
            const first = features[0]
            if (first?.geometry?.type === 'LineString') return [first.geometry.coordinates[0][0], first.geometry.coordinates[0][1]]
            if (first?.geometry?.type === 'MultiLineString') return [first.geometry.coordinates[0][0][0], first.geometry.coordinates[0][0][1]]
            return [25.7482, 62.2426]
        },
        async handleFileChange(event) {
            const file = event.target.files?.[0];
            if (file) {
                await this.$store.dispatch("routeOptimization/loadGeometryFromFile", file);
                this.$store.commit("routeOptimization/setSelectedContract", null);
                this.$store.commit("routeOptimization/setSelectedOrder", null);
                if (this.$refs.contractSelector) {
                    this.$refs.contractSelector.selectedContract = null;
                }
                if (this.$refs.orderSelector) {
                    this.$refs.orderSelector.selectedOrder = null;
                }
            }
            this.$emit("file-change", event);
        },
        enableMapSelection() {
            this.mapSelectionMode = true;
        },
        resetOptimizationState() {
            this.$store.dispatch("routeOptimization/resetSetupState");
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
            this.maxDurationHoursInput = null;
            this.maxDurationMinutesInput = null;
            this.optimizationName = "";
            this.showConfirmModal = false;
        },
        handleMapClick(latlng) {
            if (this.mapSelectionMode) {
                this.startLocation = { x: latlng.lng, y: latlng.lat };
                this.mapSelectionMode = false;
            }
        },
        onPreviewMapReady(map) {
            this.previewMap = map;
            this.renderGeometry();
            // If no geometry is loaded, snap to Finnish default location (Jyväskylä)
            // to avoid inheriting the global map's last zoom/center state
            if (!this.loadedGeometries.length) {
                this.$nextTick(() => {
                    this.previewMap.resizeMap();
                    this.previewMap.zoomToPosition(62.2426, 25.7482, 12);
                });
            }
        },
        renderGeometry() {
            if (!this.previewMap) return;

            this.previewMap.removeGeoJsonLayers();

            if (this.loadedGeometries.length > 0) {
                const hasRouteMask = !!this.routeMaskGeometry;
                // When a mask is present: draw routes fat and semi-transparent so the
                // dashed white mask overlay is visible on top
                this.loadedGeometries
                    .filter((item) => item.selected)
                    .forEach((item) => {
                        this.previewMap.addGeoJsonLayer(
                            JSON.stringify(item.geometry),
                            false,
                            item.color || '#00C896',
                            false,
                            hasRouteMask ? 0.75 : 1,
                            hasRouteMask ? 8 : (item.line_width || 2),
                            true,
                        );
                    });
                if (hasRouteMask) {
                    // Draw the mask as a dashed white overlay on top of the route lines
                    this.previewMap.addGeoJsonLayer(
                        JSON.stringify(this.routeMaskGeometry),
                        false,
                        "#ffffff",
                        false,
                        1.0,
                        3,
                        true,
                    );
                    const maskLayer = this.previewMap.geoJsonLayers[this.previewMap.geoJsonLayers.length - 1];
                    if (maskLayer) {
                        maskLayer.layer.eachLayer(l => {
                            l.setStyle({ dashArray: '6, 5', weight: 2 });
                            // Disable hover interaction on the mask layer
                            l.off('mouseover');
                            l.off('mouseout');
                        });
                    }
                }
                this.previewMap.zoomToGeoJsons();
            }
        },
        async startOptimization() {
            this.$store.commit("routeOptimization/setShowValidation", true);

            const validationMessage = this.getValidationMessage();
            if (validationMessage) {
                this.showAlert(validationMessage);
                return;
            }

            const showTranslatedAlert = (reason) => {
                const msg = reason === 'job_failed'
                    ? this.$t("route_optimization.optimization_job_failed")
                    : this.$t("route_optimization.optimization_error");
                this.showAlert(msg);
            };
            const result = await this.$store.dispatch("routeOptimization/startOptimization", showTranslatedAlert);
            // 'invalid' means canOptimize returned false — store didn't call showAlert for this case
            if (result === 'invalid') {
                this.showAlert(this.$t("route_optimization.optimization_error"));
            }
        },
        getValidationMessage() {
            // Geometry is always mandatory
            if (this.loadedGeometries.filter(g => g.selected).length === 0) {
                return this.$t("route_optimization.validation_no_geometry");
            }

            if (this.isMaxDurationMode) {
                return this.getVehicleValidationMessage(this.singleVehicle);
            } else {
                if (!this.vehicles || this.vehicles.length === 0) {
                    return this.$t("route_optimization.validation_no_vehicle");
                }
                for (const vehicle of this.vehicles) {
                    const msg = this.getVehicleValidationMessage(vehicle);
                    if (msg) return msg;
                }
            }
            return null;
        },
        getVehicleValidationMessage(vehicle) {
            if (!vehicle?.properties) {
                return this.$t("route_optimization.validation_no_vehicle");
            }
            if (!vehicle.properties.max_speed || vehicle.properties.max_speed <= 0 || vehicle.properties.max_speed >= 200) {
                return this.$t("route_optimization.validation_max_speed");
            }
            if (this.operationType === 'winter_maintenance') {
                if (!vehicle.properties.work_width || vehicle.properties.work_width <= 0 || vehicle.properties.work_width >= 1500) {
                    return this.$t("route_optimization.validation_work_width");
                }
                if (!vehicle.properties.work_speed || vehicle.properties.work_speed <= 0 || vehicle.properties.work_speed >= 200) {
                    return this.$t("route_optimization.validation_work_speed");
                }
            }
            if (!vehicle.geometry?.coordinates || !this.validateCoordinates({ x: vehicle.geometry.coordinates[0], y: vehicle.geometry.coordinates[1] })) {
                return this.$t("route_optimization.validation_start_location");
            }
            return null;
        },
        validateForm() {
            return !this.getValidationMessage();
        },
        validateVehicle(vehicle) {
            if (!vehicle || !vehicle.properties) return false;

            // Max speed: > 0 AND < 200
            if (!vehicle.properties.max_speed || vehicle.properties.max_speed <= 0 || vehicle.properties.max_speed >= 200) {
                return false;
            }

            // Working width and speed: required for winter maintenance only
            if (this.operationType === 'winter_maintenance') {
                // Working width: > 0 AND < 1500
                if (!vehicle.properties.work_width || vehicle.properties.work_width <= 0 || vehicle.properties.work_width >= 1500) {
                    return false;
                }

                // Working speed: > 0 AND < 200
                if (!vehicle.properties.work_speed || vehicle.properties.work_speed <= 0 || vehicle.properties.work_speed >= 200) {
                    return false;
                }
            }

            // Coordinates validation
            if (!vehicle.geometry || !vehicle.geometry.coordinates || !this.validateCoordinates({ x: vehicle.geometry.coordinates[0], y: vehicle.geometry.coordinates[1] })) {
                return false;
            }

            return true;
        },
        validateCoordinates(location) {
            if (!location || !location.x || !location.y) {
                return false;
            }

            const lat = parseFloat(location.y);
            const lng = parseFloat(location.x);

            // Check if coordinates are valid DD format
            if (isNaN(lat) || isNaN(lng)) {
                return false;
            }

            // Valid latitude range: -90 to 90
            // Valid longitude range: -180 to 180
            if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                return false;
            }

            return true;
        },
        toggleGeometrySelection(index, checked) {
            this.$store.dispatch("routeOptimization/toggleGeometrySelection", {
                index,
                value: checked,
            });
        },
        dispatchRouteTypeUpdate(index, value) {
            this.$store.dispatch("routeOptimization/setGeometryRouteType", { index, value });
        },
        applyRouteTypeToAll(value) {
            this.$store.dispatch("routeOptimization/setAllGeometriesRouteType", value);
            this.showRouteTypeSelection = false;
        },
        toggleGeometryRouteType(index, bit) {
            const current = this.loadedGeometries[index].route_type ?? 1;
            const hasRoad = current === 1 || current === 3;
            const hasSidewalk = current === 2 || current === 3;
            let next;
            if (bit === 1) {
                const newRoad = !hasRoad || !hasSidewalk ? true : !hasRoad;
                next = (newRoad ? 1 : 0) + (hasSidewalk ? 2 : 0) || 1;
            } else {
                const newSidewalk = !hasSidewalk || !hasRoad ? true : !hasSidewalk;
                next = (hasRoad ? 1 : 0) + (newSidewalk ? 2 : 0) || 1;
            }
            this.dispatchRouteTypeUpdate(index, next);
        },
        highlightGeometryOnMap(index) {
            if (!this.previewMap) return;
            // The selected geometries are rendered in order — find the layer index
            // by counting how many selected items come before this index
            const selectedItems = this.loadedGeometries
                .map((item, i) => ({ item, i }))
                .filter(({ item }) => item.selected);
            const layerPos = selectedItems.findIndex(({ i }) => i === index);
            if (layerPos === -1) return;
            const layerItem = this.previewMap.geoJsonLayers[layerPos];
            if (!layerItem) return;
            layerItem.layer.setStyle({ weight: 10, opacity: 1 });
        },
        unhighlightGeometryOnMap(index) {
            if (!this.previewMap) return;
            const selectedItems = this.loadedGeometries
                .map((item, i) => ({ item, i }))
                .filter(({ item }) => item.selected);
            const layerPos = selectedItems.findIndex(({ i }) => i === index);
            if (layerPos === -1) return;
            const layerItem = this.previewMap.geoJsonLayers[layerPos];
            if (!layerItem) return;
            const baseWeight = this.routeMaskGeometry ? 8 : (this.loadedGeometries[index]?.line_width || 2);
            const baseOpacity = this.routeMaskGeometry ? 0.75 : 0.95;
            layerItem.layer.setStyle({ weight: baseWeight, opacity: baseOpacity });
        },
        clearGeometry() {
            this.$store.dispatch("routeOptimization/clearGeometry");
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
            if (this.$refs.contractSelector) {
                this.$refs.contractSelector.selectedContract = null;
            }
            if (this.$refs.orderSelector) {
                this.$refs.orderSelector.selectedOrder = null;
            }
            this.renderGeometry();
        },
        handleRoutesSelected(tripsWithGeometry) {
            this.$store.dispatch("routeOptimization/loadGeometriesFromTrips", tripsWithGeometry);
            this.$store.commit("routeOptimization/setSelectedContract", null);
            this.$store.commit("routeOptimization/setSelectedOrder", null);
            if (this.$refs.contractSelector) {
                this.$refs.contractSelector.selectedContract = null;
            }
            if (this.$refs.orderSelector) {
                this.$refs.orderSelector.selectedOrder = null;
            }
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        openTripSearchModal() {
            this.$store.commit("routeOptimization/setShowSearchTripsModal", true);
        },
        closeTripSearchModal() {
            this.$store.commit("routeOptimization/setShowSearchTripsModal", false);
        },
        checkNavigationData() {
            if (this.navigationContractId && this.availableContracts.length > 0) {
                const contract = this.availableContracts.find(c => c.id == this.navigationContractId);
                if (contract && !this.selectedContract) {
                    this.selectedContract = contract;
                    this.$store.commit('routeOptimization/setNavigationData', { contractId: null, orderId: this.navigationOrderId });
                }
            }
            if (this.navigationOrderId && this.availableOrders.length > 0) {
                const order = this.availableOrders.find(o => o.id == this.navigationOrderId);
                if (order && !this.selectedOrder) {
                    this.selectedOrder = order;
                    this.$store.commit('routeOptimization/setNavigationData', { contractId: null, orderId: null });
                }
            }
        },
    },
};
</script>

<style scoped>
.filters-section {
    background: white;
    padding: 0.5rem;
}

.route-mask-status {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.92);
    padding: 8px 16px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    z-index: 500;
    font-size: 0.85rem;
    color: #555;
    pointer-events: none;
}

.preview-legend {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
    padding: 8px 12px;
    z-index: 500;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 130px;
}

.preview-legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.preview-legend-color {
    width: 18px;
    height: 4px;
    border-radius: 2px;
    flex-shrink: 0;
}

.preview-legend-color--mask {
    background: repeating-linear-gradient(
        90deg,
        #ffffff 0px,
        #ffffff 6px,
        transparent 6px,
        transparent 11px
    );
    border: 1px solid #ccc;
    border-radius: 2px;
}

.preview-legend-label {
    font-size: 0.78rem;
    color: #555;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
}

.filter-panel {
    margin-bottom: 3rem;
}

.filter-panel h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
}

.geometry-preview-container {
    position: relative;
    width: 100%;
}

.geometry-preview-container :deep(.opacity-slider-container) {
    display: none;
}

.geometry-map-wrapper {
    position: relative;
    width: 100%;
    height: 45vh;
}

.geometry-list {
    position: absolute;
    top: 12px;
    left: 12px;
    width: 520px;
    max-height: calc(45vh - 24px);
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 500;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2;
}

@media (max-width: 1199px) {
    .geometry-map-wrapper {
        height: 40vh;
    }

    .geometry-list {
        position: static;
        width: 100%;
        max-height: none;
        border-radius: 0 0 8px 8px;
        box-shadow: none;
        border: 1px solid #dee2e6;
        border-top: none;
    }
}

.geometry-list-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.4rem;
    padding: 10px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #eee;
    border-radius: 8px 8px 0 0;
    flex-shrink: 0;
}

.geometry-list-title-row {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
}

.geometry-list-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
}

.geometry-list-counts {
    font-size: 0.75rem;
    color: #6c757d;
}

.geometry-list-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.geometry-total-km {
    font-size: 1rem;
    font-weight: 600;
    color: #7FC000;
    white-space: nowrap;
}

.geometry-list-subrow {
    padding: 4px 0 0 0;
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
    user-select: none;
}

.geometry-list-subtext {
    font-size: 0.75rem;
    color: #7FC000;
}

.geometry-list-chevron {
    font-size: 0.75rem;
    color: #6c757d;
}

.geometry-items-wrapper {
    overflow-y: auto;
    transition: max-height 0.3s ease, opacity 0.3s ease;
    max-height: 600px;
    opacity: 1;
}

.geometry-items-wrapper.collapsed {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}

.route-type-selection-link {
    color: #7FC000 !important;
    font-size: 0.8rem;
    padding: 0;
    text-decoration: none;
}

.route-type-selection-link:hover {
    text-decoration: underline;
}

.route-type-selection-panel {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #eee;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.route-type-selection-label {
    font-size: 0.78rem;
    color: #6c757d;
    white-space: nowrap;
}

.geometry-items {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 6px;
    gap: 3px;
}

.geometry-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 8px;
    border-radius: 4px;
    cursor: default;
    transition: background-color 0.15s;
    min-height: 44px;
}

.geometry-item:hover {
    background: #f8f9fa;
}

.geometry-item--deselected {
    opacity: 0.5;
}

.geometry-item--deselected .form-check-input,
.geometry-item--deselected .geometry-color-dot {
    opacity: 1;
}

.geometry-item .form-check-input {
    cursor: pointer;
    flex-shrink: 0;
}

.geometry-item .form-check-input:hover {
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
}

.geometry-color-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.12) inset;
}

.geometry-item-name {
    cursor: pointer;
    margin: 0;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
}

.route-type-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

/* Default: very muted grey — looks disabled when not selected */
.route-type-badge {
    background: transparent;
    color: #adb5bd;
    border: 2px solid #ced4da;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 500;
    transition: all 0.2s;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

/* Road — blue outline when active or hovered */
.route-type-badge:hover,
.route-type-badge.active {
    background: transparent;
    border-color: #0d6efd;
    color: #0d6efd;
}

/* Sidewalk — green outline when active or hovered */
.route-type-badge--sidewalk:hover,
.route-type-badge--sidewalk.active {
    background: transparent;
    border-color: #7FC000;
    color: #7FC000;
}

/* Both — teal outline when active or hovered */
.route-type-badge--both:hover,
.route-type-badge--both.active {
    background: transparent;
    border-color: #00C896;
    color: #00C896;
}

.is-invalid {
    border-color: #dc3545 !important;
}

.input-title {
    font-size: 0.9em;
    color: #404041;
}

.setup-container {
    background: rgba(255, 255, 255, 0.95);
    color: #333;
    backdrop-filter: blur(4px);
    border-radius: 8px;
    margin: 0.5rem 0.5rem;
}

.action-button-container {
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    padding: 16px 0px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    margin: 0.5rem;
    z-index: 100;
    flex-grow: 0;
    flex-shrink: 0;
}

.loading-modal {
    position: fixed;
    background-color: rgba(255, 255, 255, 0.95);
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100001;
    margin-left: var(--sidebar-width, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    margin-left: 125px;
}

@media (max-width: 768px) {
    .loading-content {
        margin-left: 50px;
    }
}

.loading-content p {
    font-size: 2rem;
    color: #333;
}

.loading-content .info-box {
    background-color: transparent;
    border-left: none;
    max-width: 400px;
    text-align: left;
    display: block;
}

.loading-content .info-box p {
    font-size: 0.9rem;
    color: #6c757d !important;
    text-align: left;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

.spinner-routa {
    color: #7FC000;
}

.operation-type-pills {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.routa-pill {
    background: transparent;
    color: #6c757d;
    border: 2px solid #ced4da;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.routa-pill:hover {
    border-color: #7FC000;
    color: #7FC000;
    background: transparent;
}

.routa-pill.active {
    background: #7FC000;
    color: white;
    border-color: #7FC000;
}

</style>
